import React from 'react';
import {Formik, Form} from 'formik';
import FormikDropdown from 'src/components/formik-dropdown';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import {EducatorAccountSettings} from './educator-account-settings';
import Text from 'shared/components/text';
import Label from 'shared/components/label';
import InfoIcon from 'shared/components/info-icon';
import {NAME_TITLES} from 'shared/registration/constants';
import {get} from 'lodash';
import {useAccountSettingsContext} from '../../../src/components/AccountSettings/AccountSettingsContext';
import 'stylesheets/components/educator-account-settings/educator-account-settings-current-institution.scss';
import ProfileFormFooter from 'src/components/AccountSettings/profile-form/profile-form-footer/profile-form-footer';
import {useDispatch} from 'react-redux';
import {errorMessage} from 'shared/actions/notifications';

type FormFields = Pick<EducatorAccountSettings, '_id' | 'title' | 'firstName' | 'lastName'>;

interface Props {
  _id: string;
  initialValues: FormFields;
  onSubmit: (values: FormFields) => Promise<Partial<EducatorAccountSettings>>;
}

const ProfileForm = ({initialValues, onSubmit, _id}: Props) => {
  const dispatch = useDispatch();

  const {
    settings: {institution},
  } = useAccountSettingsContext<EducatorAccountSettings>();

  const mailingAddress = get(institution, 'mailingAddress', {});
  const institutionAddress = `${mailingAddress.line1} ${mailingAddress.city}, ${mailingAddress.subdivision}`;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        const errors: {firstName?: string; lastName?: string; title?: string} = {};

        ['firstName', 'lastName', 'title'].forEach((field) => {
          if (!values[field]) {
            errors[field] = 'Field is required';
          }
        });

        return errors;
      }}
    >
      {({validateForm}) => (
        <div className="account-settings-form">
          <div className="account-settings-form-title borderless">
            <Text header emphasis weight={2}>
              Edit Profile
            </Text>
          </div>
          <Form autoComplete="off">
            <legend className="shared-required-legend"></legend>
            <div className="account-settings-form-name-with-title">
              <Label required>
                Title
                <FormikDropdown name="title" options={NAME_TITLES} required />
              </Label>
              <Label required>
                First Name
                <FormikTextInput name="firstName" required showCheck placeholder="e.g. Johnnetta" />
              </Label>
              <Label required>
                Last Name
                <FormikTextInput name="lastName" required showCheck placeholder="e.g. Cole" />
              </Label>
            </div>
            <div className="educator-account-settings-current-institution">
              <h2 className="educator-account-settings-current-institution-title">
                Current Institution
              </h2>
              <p>
                {get(institution, 'name', '')}
                <br />
                {institutionAddress}
              </p>
              <p className="educator-account-settings-current-institution-disclaimer">
                <span className="educator-account-settings-current-institution-disclaimer-info-icon">
                  <InfoIcon />
                </span>
                Student privacy is our top priority, so if you are changing schools or
                organizations, the fastest way to do so is to remove your current account and sign
                up again. We will then verify your new account so you can Connect with new students.
              </p>
            </div>
          </Form>
          <ProfileFormFooter<FormFields>
            onSubmitClick={async () => {
              const errors = await validateForm();
              if (Object.keys(errors).length > 0) {
                dispatch(
                  errorMessage(`Form errors: ${Object.values(errors).join(', ').toLowerCase()}`)
                );
              }
            }}
          />
        </div>
      )}
    </Formik>
  );
};

export default ProfileForm;
